import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, Shield, Clock, KeyRound, Info } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/ui/Button';
import livePulseLogo from '../assets/live-pulse-logo.png';
import { API_PATH, request } from '../config/api';
import authStyles from '../components/auth/AuthExperience.module.css';
import styles from './ForgotPassword.module.css';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const res = await request(`${API_PATH}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim().toLowerCase() }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'Something went wrong. Please try again.');
                return;
            }
            setIsSubmitted(true);
        } catch {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const SECURITY_ITEMS = [
        { icon: <Shield size={15} />, text: 'End-to-end encrypted reset links' },
        { icon: <Clock size={15} />, text: 'Links expire in 24 hours' },
        { icon: <Mail size={15} />, text: 'Sent only to your verified email' },
    ];
    return (
        <div className={`${styles.page} ${authStyles.page}`}>
            {/* ── Left security panel ───────────────────────── */}
            <div className={`${styles.leftPanel} ${authStyles.leftPanel}`}>
                <div className={styles.orb1} />
                <div className={styles.orb2} />
                <div className={styles.orb3} />

                <Link to="/" className={styles.brandLink}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.brandName} />
                </Link>

                <div className={styles.leftContent}>
                    <div className={styles.leftBadge}>
                        <KeyRound size={13} />
                        <span>Secure account recovery</span>
                    </div>

                    <h2 className={styles.leftTitle}>
                        Account
                        <br />
                        <span className={styles.leftTitleGrad}>recovery</span>
                    </h2>

                    <p className={styles.leftDesc}>
                        We'll send a secure, one-time password reset link to your inbox. Just enter the email you signed up with.
                    </p>

                    <div className={styles.securityList}>
                        {SECURITY_ITEMS.map((item, i) => (
                            <div key={i} className={styles.securityItem} style={{ '--delay': `${280 + i * 120}ms` }}>
                                <div className={styles.securityIcon}>{item.icon}</div>
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.recoveryCard}>
                        <div className={styles.recoveryHeader}>
                            <span className={styles.mockLiveDot} />
                            <span className={styles.recoveryTitle}>Secure reset flow</span>
                        </div>
                        {[
                            { step: 'Identity check', status: 'Verified' },
                            { step: 'Token issued', status: 'Encrypted' },
                            { step: 'Access restored', status: 'On completion' },
                        ].map((item, i) => (
                            <div key={item.step} className={styles.recoveryRow} style={{ '--delay': `${620 + i * 110}ms` }}>
                                <span>{item.step}</span>
                                <strong>{item.status}</strong>
                            </div>
                        ))}
                    </div>
                </div>

                 <div className={styles.leftFooter}>© {new Date().getFullYear()} LivePulse. All rights reserved. Design & Developed by <a href="https://ottosoft.in" target="_blank" rel="noopener noreferrer">Ottosoft Private Limited</a></div>
            </div>

            {/* ── Right form panel ─────────────────────────── */}
            <div className={`${styles.rightPanel} ${authStyles.rightPanel}`}>
                <div className={styles.themeBtn}><ThemeToggle /></div>

                <div className={`${styles.formCard} ${authStyles.formCard}`}>
                    {/* Mobile-only logo */}
                    <Link to="/" className={styles.mobileLogoLink}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.mobileLogoName} />
                    </Link>

                    {!isSubmitted ? (
                        <div className={styles.formSection} key="form">
                            <div className={styles.iconWrap}>
                                <KeyRound size={24} className={styles.headerIcon} />
                            </div>

                            <h1 className={styles.title}>Forgot your password?</h1>
                            <p className={styles.subtitle}>
                                No worries — enter your email and we'll send a reset link right away.
                            </p>

                            {error && (
                                <div className={styles.errorBanner} role="alert">
                                    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zm-.75-8.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6a.875.875 0 110-1.75.875.875 0 010 1.75z" />
                                    </svg>
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className={styles.form}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="email" className={styles.label}>Email address</label>
                                    <div className={styles.inputWrapper}>
                                        <Mail size={17} className={styles.inputIcon} />
                                        <input
                                            id="email" type="email" placeholder="name@company.com"
                                            value={email} onChange={(e) => setEmail(e.target.value)}
                                            className={styles.input} required disabled={isLoading}
                                            autoComplete="email"
                                        />
                                    </div>
                                    <p className={styles.fieldHint}>Enter the same email you used to sign up.</p>
                                </div>

                                <Button
                                    type="submit"
                                    className={`${styles.submitBtn} btn-pill-primary`}
                                    disabled={isLoading}
                                    loading={isLoading}
                                    iconRight={!isLoading ? <ArrowRight size={17} /> : null}
                                >
                                    {isLoading ? 'Sending…' : 'Send reset link'}
                                </Button>
                            </form>

                            <Link to="/login" className={styles.backLink}>
                                <ArrowLeft size={15} /> Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.successSection} key="success">
                            <div className={styles.successIconWrap}>
                                <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                                    <circle cx="28" cy="28" r="28" fill="#10b981" fillOpacity="0.12" />
                                    <path d="M18 28l7 7 13-13" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h1 className={styles.title}>Check your inbox</h1>
                            <p className={styles.subtitle}>
                                We've sent a password reset link to
                                <br />
                                <strong className={styles.emailHighlight}>{email}</strong>
                            </p>
                            <div className={styles.hint}>
                                <Info size={14} className={styles.hintIcon} aria-hidden="true" />
                                <span>The link expires in 24 hours. Check your spam folder if you don't see it.</span>
                            </div>
                            <Link to="/login" className={`${styles.successBtn} btn-pill-primary`}>
                                Back to sign in <ArrowRight size={17} />
                            </Link>
                            <button className={styles.resendBtn} onClick={() => setIsSubmitted(false)}>
                                Didn't receive it? Resend
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
